import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import { getApiUrl } from '../utils/api';
import { apiFetch } from '../utils/apiFetch';

const API_URL = getApiUrl();

const moduleLabels = {
  entregas: 'Entregas',
  'cardapio-digital': 'Cardápio Digital',
  zap: 'Zap',
};

const pendingLaunchRequests = new Map();

const requestSuiteModuleLaunch = (moduleId) => {
  const pendingRequest = pendingLaunchRequests.get(moduleId);
  if (pendingRequest) return pendingRequest;

  const request = (async () => {
    try {
      const response = await apiFetch(
        `${API_URL}/api/suite/modules/${encodeURIComponent(moduleId)}/launch`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.launchUrl) {
        throw new Error(data.error || 'Falha ao preparar abertura do módulo.');
      }

      return data.launchUrl;
    } finally {
      pendingLaunchRequests.delete(moduleId);
    }
  })();

  pendingLaunchRequests.set(moduleId, request);
  return request;
};

const replaceCurrentLocation = (launchUrl) => {
  window.location.replace(launchUrl);
};

const SuiteModuleLauncher = ({
  moduleId,
  navigateToModule = replaceCurrentLocation,
}) => {
  const [attempt, setAttempt] = useState(0);
  const [launchError, setLaunchError] = useState('');

  useEffect(() => {
    let active = true;
    setLaunchError('');

    requestSuiteModuleLaunch(moduleId)
      .then((launchUrl) => {
        if (active) navigateToModule(launchUrl);
      })
      .catch((err) => {
        if (active) {
          setLaunchError(err.message || 'Falha ao preparar abertura do módulo.');
        }
      });

    return () => {
      active = false;
    };
  }, [attempt, moduleId, navigateToModule]);

  if (launchError) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', px: { xs: 2, md: 0 } }}>
        <Stack spacing={2} alignItems="flex-start">
          <Alert severity="error">{launchError}</Alert>
          <Button
            variant="contained"
            startIcon={<ReplayRoundedIcon />}
            onClick={() => {
              setLaunchError('');
              setAttempt((current) => current + 1);
            }}
          >
            Tentar novamente
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'grid',
        placeItems: 'center',
        px: 2,
      }}
    >
      <Stack spacing={2} alignItems="center">
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Abrindo {moduleLabels[moduleId] || 'módulo'}...
        </Typography>
      </Stack>
    </Box>
  );
};

export default SuiteModuleLauncher;
